import React, { useState, useEffect } from "react";
import { DatePicker, Button, message, Row, Col, Select, Spin } from "antd";
import API from "../../../api";
import urls from "../../../api/urls";
import moment from "moment";
import { dateFormat_ymd, dateFormat_dmy } from "../../../utils";
import { DownloadOutlined, FilterOutlined, FileTextOutlined } from "@ant-design/icons";
import TableContainer from "../../../components/TableContainer";
import { generateDynamicColumns } from "../../../utils/columnGenerator";
import useReportFilters from "../../../hooks/useReportFilters";
import constants from "../../../constants/constants";
import "./style.scss";

const { RangePicker } = DatePicker;
const { Option } = Select;

const disabledDate = (current) => {
  return current && current > moment().endOf("day");
};

const DeliveryMISReportNew = () => {
  const {
    filters,
    debouncedFilters,
    handleFilterChange,
    handlePageChange,
    pageNum,
    pageSize,
    setPageNum,
  } = useReportFilters({
    hub_id: undefined,
    cp_id: undefined,
  }, 500, 0);

  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [total, setTotal] = useState(0);
  const [returnedRecords, setReturnedRecords] = useState("-");
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(1, "days"),
    endDate: moment(),
  });
  const [hubs, setHubs] = useState([]);
  const [channelPartners, setChannelPartners] = useState([]);
  const [refreshSchedule, setRefreshSchedule] = useState(null);

  const userinfo = JSON.parse(localStorage.getItem("userinfo") || "{}");
  const userRole = userinfo?.roles?.[0];
  const isCPUser = [
    constants.ROLES.CP_ADMIN,
    constants.ROLES.CP_OPERATOR,
    constants.ROLES.DP_OPERATOR,
  ].includes(userRole);
  const getPartnerIdInfo = () => {
    if (userRole === constants.ROLES.CP_ADMIN || userRole === constants.ROLES.CP_OPERATOR) {
      return { apiParam: "cpId", downloadParam: "cpId", value: userinfo?.spId || null };
    } else if (userRole === constants.ROLES.DP_OPERATOR) {
      const deliveryPartners = JSON.parse(sessionStorage.getItem("deliveryPartner") || "{}")?.data || [];
      const dpId = deliveryPartners.length > 0 ? deliveryPartners[0]?.dpId : null;
      return { apiParam: "dp_id", downloadParam: "dpId", value: dpId };
    }
    return { apiParam: "cpId", downloadParam: "cpId", value: null };
  };
  const partnerInfo = getPartnerIdInfo();

  useEffect(() => {
    fetchHubs();
    fetchChannelPartners();
    fetchRefreshSchedule();
  }, []);

  const fetchRefreshSchedule = async () => {
    try {
      const res = await API.Api(urls.REFRESH_SCHEDULE_DELIVERY).get();
      if (res?.response?.data?.data?.[0]) {
        setRefreshSchedule(res.response.data.data[0]);
      }
    } catch (error) {
      console.error("Error fetching refresh schedule:", error);
    }
  };

  useEffect(() => {
    fetchData(pageNum, pageSize);
  }, [dateRange, pageNum, pageSize, debouncedFilters]);

  const fetchHubs = async () => {
    try {
      const result = await API.Api(urls.DELIVERY_MIS_REPORT_NEW_HUB_LIST).get();
      const apiResponse = result?.response?.data || result;
      
      if (apiResponse && (result?.status === true || apiResponse?.code === 200 || apiResponse?.status === true)) {
        const hubsData = Array.isArray(apiResponse.data) ? apiResponse.data : (Array.isArray(apiResponse) ? apiResponse : []);
        setHubs(hubsData);
      }
    } catch (error) {
      console.error("Error fetching hubs:", error);
    }
  };

  const fetchChannelPartners = async () => {
    try {
      const result = await API.Api(urls.DRS_STATUS_CHANNEL_PARTNER_LIST).get();
      const response = result?.response?.data;
      if (result?.status && response) {
        setChannelPartners(Array.isArray(response) ? response : (response.data || []));
      }
    } catch (error) {
      console.error("Error fetching channel partners:", error);
    }
  };

  const fetchData = async (page, size) => {
    if (!dateRange.startDate || !dateRange.endDate) return;

    setLoading(true);
    try {

      const queryParams = new URLSearchParams({
        fromDate_date: dateRange.startDate.format(dateFormat_ymd),
        toDate_date: dateRange.endDate.format(dateFormat_ymd),
        page: page.toString(),
        size: size.toString(),
      });

      if (debouncedFilters.hub_id) {
        queryParams.append("premiseId", debouncedFilters.hub_id);
      }
      if (isCPUser && partnerInfo.value) {
        queryParams.append(partnerInfo.apiParam, partnerInfo.value);
      } else if (debouncedFilters.cp_id) {
        queryParams.append("cpId", debouncedFilters.cp_id);
      }

      const url = `${urls.DELIVERY_MIS_REPORT_NEW_2}?${queryParams.toString()}`;
      const result = await API.Api(url).get();

      const status = result?.response?.status;
      const response = result?.response?.data;
      const responsePageSize = response?.pagination?.pageSize;
      const responseTotalRecords = response?.pagination?.totalRecords;

      if (status === 200) {
        setTableData(response.data || []);
        setTotal(responseTotalRecords || 0);
        setReturnedRecords(
          (responsePageSize < responseTotalRecords ? responsePageSize : responseTotalRecords) ?? 0
        );
      } else {
        setTableData([]);
        setTotal(0);
        message.error(response?.message || "Failed to fetch data");
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      message.error("Failed to fetch report data");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      message.warning("Please select a date range");
      return;
    }

    const queryParams = new URLSearchParams({
      fromDate_date: dateRange.startDate.format(dateFormat_ymd),
      toDate_date: dateRange.endDate.format(dateFormat_ymd),
    });

    if (debouncedFilters.hub_id) {
      queryParams.append("premiseId", debouncedFilters.hub_id);
    }
    if (isCPUser && partnerInfo.value) {
      queryParams.append(partnerInfo.downloadParam, partnerInfo.value);
    } else if (debouncedFilters.cp_id) {
      queryParams.append("cpId", debouncedFilters.cp_id);
    }

    const downloadUrl = `${urls.DELIVERY_MIS_REPORT_NEW_DOWNLOAD}?${queryParams.toString()}`;
    window.open(downloadUrl, "_blank");
  };

  const handleDateChange = (dates) => {
    if (dates) {
      setDateRange({ startDate: dates[0], endDate: dates[1] });
    } else {
      setDateRange({ startDate: null, endDate: null });
    }
    setPageNum(1);
  };

  const staticColumns = [
    {
      title: "Shipments Outscanned",
      dataIndex: "shipments_outscanned",
      key: "shipments_outscanned",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-outscan",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(
                `/drs-clickable-detail?${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "Inscanned Same Day",
      dataIndex: "shipments_inscanned_on_same_day",
      key: "shipments_inscanned_on_same_day",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-inscan-same-day",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(
                `/drs-clickable-detail?${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "Not Inscanned Same Day",
      dataIndex: "shipments_not_inscanned_on_same_day",
      key: "shipments_not_inscanned_on_same_day",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-not-inscan-same-day",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(
                `/drs-clickable-detail?${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "OFD Same Day",
      dataIndex: "shipments_ofd_on_same_day",
      key: "shipments_ofd_on_same_day",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-ofd-same-day",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(
                `/drs-clickable-detail?${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "Delivered Same Day",
      dataIndex: "shipments_delivered_on_same_day",
      key: "shipments_delivered_on_same_day",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-delivered-same-day",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(
                `/drs-clickable-detail?${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "Undelivered Same Day",
      dataIndex: "shipments_undelivered_on_same_day",
      key: "shipments_undelivered_on_same_day",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-undelivered-same-day",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(
                `/drs-clickable-detail?${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "RTO OFD Same Day",
      dataIndex: "shipments_rto_ofd_on_same_day",
      key: "shipments_rto_ofd_on_same_day",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-rto-ofd-same-day",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(
                `/drs-clickable-detail?${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "RTO Initiated Same Day",
      dataIndex: "shipments_rto_initiated_on_same_day",
      key: "shipments_rto_initiated_on_same_day",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-rto-initiated-same-day",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(
                `/drs-clickable-detail?${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "RTO Delivered Same Day",
      dataIndex: "shipments_rto_delivered_on_same_day",
      key: "shipments_rto_delivered_on_same_day",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-rto-delivered-same-day",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(
                `/drs-clickable-detail?${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "RTO Undelivered Same Day",
      dataIndex: "shipments_rto_undelivered_on_same_day",
      key: "shipments_rto_undelivered_on_same_day",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-rto-undelivered-same-day",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(
                `/drs-clickable-detail?${queryParams.toString()}`,
                "_blank",
              );
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "NDD",
      dataIndex: "ndd",
      key: "ndd",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-new-ndd",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(`/drs-clickable-detail?${queryParams.toString()}`, "_blank");
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "NDD Plus 1",
      dataIndex: "ndd_plus_1",
      key: "ndd_plus_1",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-new-ndd-1",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(`/drs-clickable-detail?${queryParams.toString()}`, "_blank");
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "NDD Plus 2",
      dataIndex: "ndd_plus_2",
      key: "ndd_plus_2",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-new-ndd-2",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(`/drs-clickable-detail?${queryParams.toString()}`, "_blank");
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "NDD Plus 3",
      dataIndex: "ndd_plus_3",
      key: "ndd_plus_3",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-new-ndd-3",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(`/drs-clickable-detail?${queryParams.toString()}`, "_blank");
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "NDD Plus 4",
      dataIndex: "ndd_plus_4",
      key: "ndd_plus_4",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-new-ndd-4",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(`/drs-clickable-detail?${queryParams.toString()}`, "_blank");
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "NDD Greater 4",
      dataIndex: "ndd_greater_4",
      key: "ndd_greater_4",
      align: "center",
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const queryParams = new URLSearchParams({
                type: "mis-new-ndd-4-plus",
                fromDate_outscan_date: record.date,
                toDate_outscan_date: record.date,
                from_hub_id: record.premise_id,
                to_channel_partner: record.channel_partner,
              });
              window.open(`/drs-clickable-detail?${queryParams.toString()}`, "_blank");
            }}
          >
            {text}
          </span>
        );
      },
    },
  ];

  const columns = React.useMemo(
    () => generateDynamicColumns(tableData, staticColumns),
    [tableData, staticColumns],
  );

  return (
    <div className="delivery-mis-report-container">
      {/* ── Page Top Bar ── */}
      <div className="page-topbar">
        <div className="topbar-left">
          <span className="title-main">Delivery MIS Report</span>
          {refreshSchedule && (
            <div className="sync-indicators">
              <div className="sync-tag last-refresh">
                <span className="sync-label">Last Refresh</span>
                <span className="sync-time">
                  {moment(refreshSchedule.dag_last_completion_time_ist).format(
                    "DD-MM-YYYY | HH:mm:ss",
                  )}
                </span>
              </div>
              <div className="sync-tag next-sync">
                <span className="sync-label">Next Sync</span>
                <span className="sync-time">
                  {moment(refreshSchedule.dag_next_run_ist).format(
                    "DD-MM-YYYY | HH:mm:ss",
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="topbar-actions">
          <div className="filter-item">
            <span className="filter-label">Outscan Date</span>
            <RangePicker
              value={[dateRange.startDate, dateRange.endDate]}
              format={dateFormat_dmy}
              onChange={handleDateChange}
              disabledDate={disabledDate}
              allowClear={false}
            />
          </div>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            className="download-btn"
          >
            Download
          </Button>
        </div>
      </div>

      {/* ── Summary Hero Band ── */}
      <div className="summary-hero-band">
        <div className="hero-left">
          <div className="hero-icon">
            <FileTextOutlined />
          </div>
          <div className="hero-text">
            <div className="hero-label">MIS Summary</div>
            <div className="hero-desc">
              Comprehensive delivery performance metrics and same-day efficiency
              analysis
            </div>
          </div>
        </div>
        <div className="hero-stats">
          <div className="hero-stat">
            <div className="hero-stat-value">
              {total ? total.toLocaleString() : "0"}
            </div>
            <div className="hero-stat-label">Total Records</div>
          </div>
          <div className="hero-divider" />
          <div className="hero-stat">
            <div className="hero-stat-value">
              {typeof returnedRecords === "number"
                ? returnedRecords.toLocaleString()
                : returnedRecords}
            </div>
            <div className="hero-stat-label">Showing</div>
          </div>
        </div>
      </div>

      {/* ── Filters Panel ── */}
      <div className="filters-panel">
        <div className="filters-header">
          <FilterOutlined className="filter-icon" />
          <span className="filters-title">Data Filters</span>
        </div>
        <div className="filters-body">
          <Row gutter={[16, 14]}>
            <Col xs={24} sm={12} md={8} lg={6} className="filter-col">
              <span className="filter-field-label">Hub</span>
              <Select
                allowClear
                showSearch
                placeholder="Select Hub"
                style={{ width: "100%" }}
                value={filters.hub_id}
                onChange={(value) => handleFilterChange("hub_id", value)}
                optionFilterProp="children"
              >
                {hubs.map((hub, idx) => {
                  const label = hub?.premiseName || hub?.hub_name || hub?.hub || (typeof hub === "string" ? hub : "");
                  const value = hub?.premiseId || hub?.hub_id || hub?.premise_id || (typeof hub === "string" ? hub : idx);
                  return (
                    <Option key={value || idx} value={value}>
                      {String(label || value || idx)}
                    </Option>
                  );
                })}
              </Select>
            </Col>
            {!isCPUser && (
              <Col xs={24} sm={12} md={8} lg={6} className="filter-col">
                <span className="filter-field-label">Channel Partner</span>
                <Select
                  allowClear
                  showSearch
                  placeholder="Select CP"
                  style={{ width: "100%" }}
                  value={filters.cp_id}
                  onChange={(value) => handleFilterChange("cp_id", value)}
                  optionFilterProp="children"
                >
                  {channelPartners.map((cp, idx) => {
                    const label =
                      cp?.channel_partner || (typeof cp === "string" ? cp : "");
                    const value =
                      cp?.cp_id || (typeof cp === "string" ? cp : idx);
                    return (
                      <Option key={value || idx} value={value}>
                        {String(label || value || idx)}
                      </Option>
                    );
                  })}
                </Select>
              </Col>
            )}
          </Row>
        </div>
      </div>

      {/* ── Data Table ── */}
      <Spin spinning={loading}>
        <div className="table-panel">
          <TableContainer
            columns={columns}
            dataSource={tableData}
            pagination={{
              current: pageNum + 1,
              pageSize: pageSize,
              total: total || 0,
              showSizeChanger: true,
              pageSizeOptions: ["50", "100", "150", "200"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
            onChange={(pagination) => handlePageChange(pagination.current - 1, pagination.pageSize)}
            scroll={{ x: "max-content" }}
            rowKey={(record, index) =>
              `${record.date}_${record.channel_partner}_${index}`
            }
          />
        </div>
      </Spin>
    </div>
  );
};

export default DeliveryMISReportNew;
